import {BulkUserModificationError} from "@/api";
import {BULK_USER_EXCEL_TRANSLATIONS} from "./common";
import {defineMessages, FormattedMessage} from "react-intl";
import {makeStyles} from "tss-react/mui";
import {Table, TableBody, TableCell, TableContainer, TableRow} from "@mui/material";
import FormattedTableHead from "@/components/FormattedTableHead";
import {useMemo} from "react";
import FormattedCustomValueError from "@/components/FormattedCustomValueError";

/** Defines a column in the error table */
type Column = {
    field: keyof BulkUserModificationError;
    label: keyof typeof headerMessages;
    renderer?: (error: BulkUserModificationError) => React.ReactNode;
};

/** Columns to display in the error table */
const columns: Column[] = [
    {
        field: "sheet",
        label: "sheet",
        renderer: ({sheet}) => <FormattedMessage {...SHEET_NUMBER_NAME[sheet as 1 | 3]} />,
    } as Column,
    {
        field: "column",
        label: "column",
        // Convert 1-based index to column letter (1 -> A, 2 -> B, etc.)
        renderer: ({column}) => (column ? String.fromCharCode(64 + column) : null),
    } as Column,
    {
        field: "row",
        label: "row",
        renderer: ({row}) => row + 2, // +1 for header row, +1 for 0-based index
    } as Column,
    {
        field: "type",
        label: "error",
        renderer: (error) => <FormattedCustomValueError error={error} />,
    } as Column,
] as const;

/** Mapping of sheet numbers to their display names */
const SHEET_NUMBER_NAME = {
    1: BULK_USER_EXCEL_TRANSLATIONS.users,
    3: BULK_USER_EXCEL_TRANSLATIONS.locations,
} as const;

// Some styles for the table
const tableStyles = makeStyles()((theme) => ({
    tableContainer: {
        marginTop: theme.spacing(2),
        overflowX: "auto",
        maxHeight: 400,
        "& table > tbody > tr:nth-of-type(odd)": {
            backgroundColor: theme.palette.grey[100],
        },
    },
    table: {
        minWidth: 650,
        borderCollapse: "collapse",
    },
    tableCell: {
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1),
        "&.MuiTableCell-stickyHeader": {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.error.contrastText,
        },
    },
}));

const headerMessages = defineMessages({
    sheet: {
        id: "bulk_user_modification.excel.sheet",
        defaultMessage: "Sheet",
    },
    column: {
        id: "bulk_user_modification.excel.column",
        defaultMessage: "Column",
    },
    row: {
        id: "bulk_user_modification.excel.row",
        defaultMessage: "Row",
    },
    error: {
        id: "bulk_user_modification.excel.error",
        defaultMessage: "Error",
    },
});

type ErrorTableProps = {
    errors: BulkUserModificationError[];
};

/** Component to display a table of bulk user modification errors */
export const BulkUserErrorTable: React.FC<ErrorTableProps> = ({errors}) => {
    const {classes} = tableStyles();

    const headerCells = useMemo(
        () =>
            columns.map((col) => ({
                label: headerMessages[col.label],
                props: {className: classes.tableCell},
            })),
        [classes.tableCell],
    );

    return (
        <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} stickyHeader>
                <FormattedTableHead cells={headerCells} />
                <TableBody>
                    {errors.map((error, index) => {
                        return (
                            <TableRow key={index}>
                                {columns.map((col, colIndex) => {
                                    const value = error[col.field];
                                    return (
                                        <TableCell key={colIndex} className={classes.tableCell}>
                                            {col.renderer ? col.renderer(error) : value}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
